'use client';

import { useMemo, useState, useTransition } from 'react';

import { useRouter } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';

import { DetailsDrawer } from '~/(tenant)/_components/details-drawer';
import { SeasonPriceBlock } from '~/components/class-seasons/season-price-block';
import { SeasonScheduleList } from '~/components/class-seasons/season-schedule-list';
import { getSeasonUnavailableReason } from '~/lib/class-seasons/class-season-quote';

import {
  pickPolicyText,
  policyTextToBullets,
  type ResolvedBookingPolicy,
} from '../_lib/booking-policy-config';
import {
  createClassSeasonCheckoutAction,
  purchaseClassSeasonWithWalletAction,
} from '../_lib/server/class-season-actions';
import type { ClassSeasonDetails } from '../_lib/server/class-season.loader';
import type { SelectedAccount } from './account-multi-selector';
import { useBookingPresentation } from './booking-presentation';
import { HyperpayCheckoutDialog } from './hyperpay-checkout-dialog';
import { MoyasarCheckoutDialog } from './moyasar-checkout-dialog';
import { PackageCheckoutDialog } from './package-checkout-dialog';

type MoyasarCheckoutData = React.ComponentProps<
  typeof MoyasarCheckoutDialog
>['checkoutData'];

interface SeasonFlowState {
  step: 'details' | 'checkout' | 'payment';
  moyasar: {
    data: MoyasarCheckoutData;
    returnUrl: string;
    amount: number;
  } | null;
  hyperpay: {
    sessionId: string;
    integrity?: string;
    provider: 'hyperpay';
    returnPath: string;
  } | null;
}

export interface ClassSeasonFlowProps {
  bookingPolicy: ResolvedBookingPolicy;
  season: ClassSeasonDetails;
  /** 'checkout' skips the season drawer when the customer already chose
   *  the whole season on the booking page. */
  initialStep?: 'details' | 'checkout';
  /** Called once the drawer, checkout and payment dialogs are all closed. */
  onDone: () => void;
  onWalletPaid: (newBalance: number) => void;
  isAuthenticated: boolean;
  userId?: string;
  userName?: string;
  tenantId?: string;
  tenantName?: string;
  publishableKey?: string | null;
  provider?: string | null;
  wallet: { enabled: boolean; balance: number };
}

// YYYY-MM-DD as local noon so no timezone fold moves it to another day.
export function parseSeasonDate(date: string) {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
}

/**
 * Display name (schedule name, else the localized class name) and the
 * "Class · Wednesday · 10:00 PM · Coach" line of a season.
 */
export function describeClassSeason(
  season: ClassSeasonDetails,
  language: string,
) {
  const className =
    language === 'ar' && season.classNameAlt
      ? season.classNameAlt
      : season.className;
  const firstSession = season.quote.sessions[0];

  return {
    seasonName: season.scheduleName || className,
    subtitle: [
      season.scheduleName ? className : null,
      firstSession
        ? formatLocaleDate(parseSeasonDate(firstSession.date), language, {
            weekday: 'long',
            month: undefined,
            day: undefined,
            year: undefined,
          })
        : null,
      firstSession ? formatTimeToAmPm(firstSession.start_time, language) : null,
      season.trainerName,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

/**
 * Season Register drawer (price block and schedule with the sessions not
 * covered struck) followed by the shared checkout (card, HyperPay, wallet).
 * Paying registers the customer in every remaining session from the season's
 * start session; the webhook / wallet RPC does the registration.
 */
export function ClassSeasonFlow({
  season,
  initialStep = 'details',
  onDone,
  onWalletPaid,
  isAuthenticated,
  userId,
  userName,
  tenantId,
  tenantName,
  publishableKey,
  provider,
  wallet,
  bookingPolicy,
}: ClassSeasonFlowProps) {
  const presentation = useBookingPresentation();
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const [isPurchasing, startPurchasing] = useTransition();
  const [flow, setFlow] = useState<SeasonFlowState>({
    step: initialStep,
    moyasar: null,
    hyperpay: null,
  });

  const { quote } = season;
  const { seasonName, subtitle } = useMemo(
    () => describeClassSeason(season, i18n.language),
    [season, i18n.language],
  );
  const unavailable = getSeasonUnavailableReason(quote);
  const cancellationText = pickPolicyText(
    bookingPolicy.cancellationPolicy,
    i18n.language,
  );

  function openCheckout() {
    if (!isAuthenticated) {
      toast.error(t('bookings:fixedSlot.toastSignInRequired'));
      return;
    }
    setFlow((prev) => ({ ...prev, step: 'checkout' }));
  }

  // Apple Pay charges Moyasar straight from the browser, so it takes the
  // same server-validated season metadata as the card checkout and returns
  // to the season return page instead of the package one.
  async function resolveApplePayCheckout(args: {
    selectedAccounts: SelectedAccount[];
  }): Promise<
    | { success: true; metadata: Record<string, string>; returnPath: string }
    | { success: false; error: string }
  > {
    if (provider !== 'moyasar' || args.selectedAccounts.length > 1) {
      return {
        success: false,
        error: t('bookings:fixedSlot.toastFailedCheckout'),
      };
    }

    try {
      const result = await createClassSeasonCheckoutAction({
        classScheduleId: season.classScheduleId,
        startBookingId: season.startBookingId ?? undefined,
        price: quote.price,
        returnUrl: `${window.location.origin}/book/return`,
        purchaseForUserId: args.selectedAccounts[0]?.id,
      });

      return result.success && result.data.provider === 'moyasar'
        ? {
            success: true,
            metadata: result.data.metadata,
            returnPath: result.data.returnUrl.replace(
              window.location.origin,
              '',
            ),
          }
        : {
            success: false,
            error: result.success
              ? t('bookings:fixedSlot.toastFailedCheckout')
              : result.error,
          };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : t('bookings:fixedSlot.toastFailedCheckout'),
      };
    }
  }

  function handleCheckout(
    _packageId: string,
    _playerCount: number | undefined,
    price: number | undefined,
    selectedAccounts: SelectedAccount[] | undefined,
  ) {
    startPurchasing(async () => {
      try {
        const result = await createClassSeasonCheckoutAction({
          classScheduleId: season.classScheduleId,
          startBookingId: season.startBookingId ?? undefined,
          price,
          returnUrl: `${window.location.origin}/book/return`,
          purchaseForUserId: selectedAccounts?.[0]?.id,
        });

        if (!result.success) {
          toast.error(result.error);
          return;
        }

        const { data } = result;
        const checkout = 'checkout' in data ? data.checkout : undefined;

        if (data.provider === 'hyperpay') {
          if (checkout?.mode !== 'embedded_widget' ||
            checkout.provider !== 'hyperpay' || !checkout.checkoutId) {
            toast.error(t('bookings:fixedSlot.toastFailedCheckout'));
            return;
          }
          setFlow((prev) => ({
            ...prev,
            step: 'payment',
            hyperpay: {
              sessionId: checkout.checkoutId,
              integrity: checkout.integrity,
              provider: 'hyperpay',
              returnPath: data.returnUrl.replace(window.location.origin, ''),
            },
          }));
          return;
        }

        if (data.provider !== 'moyasar' || !data.publishableKey) {
          toast.error(t('bookings:fixedSlot.toastFailedCheckout'));
          return;
        }

        // No packageData: the Moyasar dialog must not run its package
        // pre-redirect save for a season charge.
        setFlow((prev) => ({
          ...prev,
          step: 'payment',
          moyasar: {
            returnUrl: data.returnUrl,
            amount: data.amount,
            data: {
              sessionId: data.purchaseId,
              provider: data.provider,
              purchaseId: data.purchaseId,
              publishableKey: data.publishableKey,
              userId: data.userId,
              tenantId: data.tenantId,
              tenantName: data.tenantName,
              locationId: data.locationId,
              extraMetadata: data.metadata,
            },
          },
        }));
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : t('bookings:fixedSlot.toastFailedCheckout'),
        );
      }
    });
  }

  function handleWalletCheckout(
    _packageId: string,
    _playerCount: number | undefined,
    price: number | undefined,
    selectedAccounts: SelectedAccount[],
    _discountCode: string | undefined,
    _playerDetails: unknown,
    _startDate: string | undefined,
    purchaseReference: string,
  ) {
    const recipientId = selectedAccounts[0]?.id ?? userId;

    if (!recipientId) {
      toast.error(t('bookings:toastSelectAccount'));
      return Promise.resolve(false);
    }

    // Resolving true lets the checkout dialog retire its stored purchase
    // reference; a kept reference would replay this purchase on a re-buy.
    return new Promise<boolean>((resolve) => {
      startPurchasing(async () => {
        let succeeded = false;

        try {
          const result = await purchaseClassSeasonWithWalletAction({
            classScheduleId: season.classScheduleId,
            startBookingId: season.startBookingId ?? undefined,
            price,
            purchaseForUserId: recipientId,
            purchaseReference,
          });

          if (!result.success) {
            toast.error(result.error);
            return;
          }

          succeeded = true;
          if (result.data.new_balance !== null) {
            onWalletPaid(result.data.new_balance);
          }
          toast.success(t('bookings:season.registered'));
          onDone();
          router.refresh();
        } catch (error) {
          toast.error(error instanceof Error
            ? error.message
            : t('bookings:fixedSlot.toastFailedCheckout'));
        } finally {
          resolve(succeeded);
        }
      });
    });
  }

  return (
    <>
      <DetailsDrawer
        contentClassName={presentation.className}
        contentStyle={presentation.style}
        open={flow.step === 'details'}
        onOpenChange={(open) => {
          if (!open) onDone();
        }}
        name={seasonName}
        subtitle={subtitle}
        imageUrl={season.imageUrl}
        description={
          i18n.language === 'ar' && season.descriptionAlt
            ? season.descriptionAlt
            : season.description
        }
        footer={
          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-base font-semibold">
                {t('bookings:season.price')}
              </h3>
              <SeasonPriceBlock quote={quote} />
            </div>

            {unavailable ? (
              <p className="text-muted-foreground text-sm" role="status">
                {t(`bookings:season.unavailable.${unavailable}`)}
              </p>
            ) : null}

            <Button
              size="lg"
              className="w-full"
              disabled={!!unavailable}
              onClick={openCheckout}
              data-test="season-register-button"
            >
              {t('bookings:season.register')}
            </Button>

            <div className="space-y-1">
              <h3 className="text-base font-semibold">
                {t('bookings:season.schedule')}
              </h3>
              <SeasonScheduleList sessions={quote.sessions} />
            </div>
          </div>
        }
      />

      <PackageCheckoutDialog
        bookingPolicy={{
          requireAcceptance: bookingPolicy.requireAcceptance,
          termsText: pickPolicyText(bookingPolicy.termsConditions, i18n.language),
          cancellationPolicyItems: cancellationText
            ? policyTextToBullets(cancellationText)
            : [t('bookings:season.cancellationNotice')],
          purchaseKey: `${season.classScheduleId}:${season.startBookingId ?? ''}:${quote.starts_on}:${quote.ends_on}`,
        }}
        isOpen={flow.step === 'checkout'}
        onClose={onDone}
        packageData={{
          id: season.classScheduleId,
          name: seasonName,
          price: quote.price,
          original_price: quote.prorated ? quote.full_price : null,
          booking_count: quote.remaining_sessions,
          package_type: 'group_classes',
          description: null,
          features: [],
          color: null,
          player_pricing: null,
          max_subscribers: null,
          fixed_starts_at: quote.starts_on,
          fixed_ends_at: quote.ends_on,
          // Same checkout contract as fixed-slot: single recipient, no
          // discount codes or start date, Apple Pay via the resolver.
          is_fixed_slot: true,
        }}
        onCheckout={handleCheckout}
        resolveFixedSlotApplePayMetadata={resolveApplePayCheckout}
        onWalletCheckout={handleWalletCheckout}
        isProcessing={isPurchasing}
        isAuthenticated={isAuthenticated}
        userId={userId}
        userName={userName}
        tenantId={tenantId}
        tenantName={tenantName}
        publishableKey={publishableKey}
        provider={provider}
        locationId={season.locationId ?? undefined}
        walletEnabled={wallet.enabled}
        walletBalance={wallet.balance}
        allowDiscountCodes={false}
      />

      <MoyasarCheckoutDialog
        isOpen={!!flow.moyasar}
        onClose={onDone}
        checkoutData={flow.moyasar?.data ?? null}
        returnUrl={flow.moyasar?.returnUrl}
        bookingDetails={
          flow.moyasar
            ? {
                amount: flow.moyasar.amount,
                description: t('bookings:fixedSlot.paymentDescription', {
                  name: seasonName,
                }),
              }
            : null
        }
        onSuccess={onDone}
      />

      {flow.hyperpay ? (
        <HyperpayCheckoutDialog
          isOpen
          checkoutData={flow.hyperpay}
          returnPath={flow.hyperpay.returnPath}
          onClose={onDone}
          onSuccess={onDone}
        />
      ) : null}
    </>
  );
}
